// src/pages/auth/Login.jsx
import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSettings } from "../../context/SettingsContext";
import { useThemeContext } from "../../context/ThemeContext";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  Link,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  Fade,
  Slide,
  useMediaQuery,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  PersonAdd,
  ArrowForward,
  Security,
} from "@mui/icons-material";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { siteName, siteLogo } = useSettings();
  const { colors } = useThemeContext();

  const isMobile = useMediaQuery("(max-width:600px)");
  const safeSiteName = siteName || "V-Tech"; // ✅ SAFE FALLBACK

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const primaryColor = colors?.primaryColor || "#E31E24";
  const primaryDark = colors?.primaryColor
    ? darkenColor(colors.primaryColor, 20)
    : "#B71C1C";
  const isDark = colors?.themeMode === "dark";

  function darkenColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max((num >> 16) - amt, 0);
    const G = Math.max(((num >> 8) & 0x00ff) - amt, 0);
    const B = Math.max((num & 0x0000ff) - amt, 0);
    return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const result = await login(formData.email, formData.password, rememberMe);
    if (result.success) {
      if (result.user.role === "admin") navigate("/admin/dashboard");
      else if (result.user.role === "vendor") navigate("/vendor/dashboard");
    } else {
      if (
        result.error &&
        result.error.includes("Vendors and Administrators only")
      ) {
        setError(
          "Access denied. This portal is for Vendors and Administrators only.",
        );
      } else setError(result.error || "Invalid email or password");
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isDark
          ? "linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #16213e 100%)"
          : `linear-gradient(135deg, ${primaryColor}08 0%, #f0f0f5 50%, #ffffff 100%)`,
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${primaryColor}15 0%, transparent 70%)`,
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "33%": { transform: "translate(-30px, -50px) scale(1.1)" },
            "66%": { transform: "translate(30px, 20px) scale(0.9)" },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${primaryColor}10 0%, transparent 70%)`,
          animation: "float 25s ease-in-out infinite reverse",
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Slide direction="up" in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              width: "100%",
              borderRadius: { xs: 3, sm: 4 },
              bgcolor: isDark
                ? "rgba(30, 30, 46, 0.92)"
                : "rgba(255, 255, 255, 0.88)",
              backdropFilter: "blur(20px)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.4)"}`,
              boxShadow: isDark
                ? "0 30px 80px rgba(0,0,0,0.5)"
                : "0 30px 80px rgba(0,0,0,0.08)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${primaryColor}, ${primaryDark}, ${primaryColor})`,
                backgroundSize: "200% 100%",
                animation: "gradientMove 3s ease-in-out infinite",
                "@keyframes gradientMove": {
                  "0%": { backgroundPosition: "0% 50%" },
                  "50%": { backgroundPosition: "100% 50%" },
                  "100%": { backgroundPosition: "0% 50%" },
                },
              }}
            />

            <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
              <Box sx={{ mb: 2 }}>
                {siteLogo ? (
                  <Box
                    sx={{
                      width: isMobile ? 80 : 120,
                      height: isMobile ? 70 : 100,
                      mx: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {/* ✅ FIX: Use siteLogo DIRECTLY */}
                    <img
                      src={siteLogo}
                      alt={safeSiteName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: isMobile ? 70 : 80,
                      height: isMobile ? 70 : 80,
                      mx: "auto",
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                      borderRadius: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 12px 40px ${primaryColor}4D`,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h3" : "h2"}
                      sx={{ color: "white", fontWeight: 900, letterSpacing: 1 }}
                    >
                      {/* ✅ FIX: Use safeSiteName */}
                      {safeSiteName.charAt(0)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="800"
                sx={{
                  letterSpacing: -0.5,
                  color: isDark ? "#ffffff" : "#1A1A1A",
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 1,
                  fontWeight: 500,
                  color: isDark ? "#aaaaaa" : "text.secondary",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Sign in to your {safeSiteName} account
              </Typography>
            </Box>

            {error && (
              <Fade in>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    alignItems: "center",
                    bgcolor: isDark ? "rgba(244, 67, 54, 0.15)" : undefined,
                    color: isDark ? "#ef5350" : undefined,
                    border: `1px solid ${isDark ? "rgba(244, 67, 54, 0.2)" : "error.main"}`,
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                      "&:hover fieldset": { borderColor: primaryColor },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryColor,
                        borderWidth: 2,
                      },
                      "&.Mui-focused": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: primaryColor,
                    },
                    "& .MuiInputBase-input": {
                      color: isDark ? "#ffffff" : undefined,
                      py: { xs: 1.5, sm: 2 },
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#aaaaaa" : undefined,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined
                          sx={{ color: isDark ? "#666" : "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                      "&:hover fieldset": { borderColor: primaryColor },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryColor,
                        borderWidth: 2,
                      },
                      "&.Mui-focused": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: primaryColor,
                    },
                    "& .MuiInputBase-input": {
                      color: isDark ? "#ffffff" : undefined,
                      py: { xs: 1.5, sm: 2 },
                    },
                    "& .MuiInputLabel-root": {
                      color: isDark ? "#aaaaaa" : undefined,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined
                          sx={{ color: isDark ? "#666" : "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: isDark ? "#aaa" : "text.secondary",
                            "&:hover": { color: primaryColor },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        sx={{
                          color: primaryColor,
                          "&.Mui-checked": { color: primaryColor },
                          "& .MuiSvgIcon-root": { fontSize: 20 },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDark ? "#aaaaaa" : "text.secondary",
                          fontWeight: 500,
                          fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        }}
                      >
                        Keep me signed in
                      </Typography>
                    }
                  />
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    underline="hover"
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      fontWeight: 600,
                      color: isDark ? "#aaaaaa" : "text.secondary",
                      "&:hover": { color: primaryColor },
                      transition: "color 0.3s ease",
                    }}
                  >
                    Forgot password?
                  </Link>
                </Stack>
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{
                    py: { xs: 1.5, sm: 1.8 },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: 15, sm: 16 },
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
                    color: "white",
                    boxShadow: `0 8px 24px ${primaryColor}4D`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 12px 40px ${primaryColor}66`,
                      transform: "translateY(-2px)",
                    },
                    "&:active": { transform: "translateY(0)" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </Stack>
            </form>

            <Divider
              sx={{
                my: { xs: 3, sm: 4 },
                "&::before, &::after": {
                  borderColor: isDark ? "rgba(255,255,255,0.1)" : undefined,
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  px: 2,
                  fontWeight: 500,
                  color: isDark ? "#aaaaaa" : "text.secondary",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                New to {safeSiteName}?
              </Typography>
            </Divider>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
              flexWrap="wrap"
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: isDark ? "#aaaaaa" : "text.secondary",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                Create your vendor account today
              </Typography>
              <Link
                component={RouterLink}
                to="/register"
                underline="none"
                sx={{
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: primaryColor,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  "&:hover": { color: primaryDark },
                  transition: "color 0.3s ease",
                }}
              >
                <PersonAdd fontSize="small" />
                Register Now
                <ArrowForward fontSize="small" />
              </Link>
            </Stack>
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? "#666" : "text.disabled",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                <Security sx={{ fontSize: 14 }} />
                Secured by V-Tech Security
              </Typography>
            </Box>
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
};

export default Login;
